export const SEO_TITLE_MAX_LEN = 70;
export const SEO_TITLE_SUFFIX = ' | Zarpar';
export const SEO_TITLE_SEPARATOR = ' - ';

export function normalizeSEOTitleText(text: string): string {
	return text.replace(/\s+/g, ' ').trim();
}

export function truncateSEOTitleAtWord(text: string, maxChars: number): string {
	const ellipsis = '…';
	if (text.length <= maxChars) return text;
	if (maxChars <= ellipsis.length + 4) return text.slice(0, maxChars);
	const limit = maxChars - ellipsis.length;
	const slice = text.slice(0, limit);
	const lastSpace = slice.lastIndexOf(' ');
	const cut = lastSpace > Math.floor(limit * 0.5) ? lastSpace : limit;
	return slice.slice(0, cut).trimEnd() + ellipsis;
}

export interface BuildSeoTitleOptions {
	maxLen?: number;
	suffix?: string;
	separator?: string;
	includeSuffix?: boolean;
}

/**
 * Builds a title within a character budget. Core is required; optional pieces are
 * added greedily while they fit. Suffix (e.g. " | Zarpar") is appended only if it fits.
 *
 * When the core is long enough that title + suffix would not fit, the full budget is
 * given to the title alone (no optional pieces, no reserved suffix space).
 */
export function buildSeoTitle(
	core: string,
	optional: string[] = [],
	opts: BuildSeoTitleOptions = {}
): string {
	const maxLen = opts.maxLen ?? SEO_TITLE_MAX_LEN;
	const suffix = opts.includeSuffix === false ? '' : (opts.suffix ?? SEO_TITLE_SUFFIX);
	const separator = opts.separator ?? SEO_TITLE_SEPARATOR;

	const normalizedCore = normalizeSEOTitleText(core);
	if (!normalizedCore) return truncateSEOTitleAtWord('Zarpar', maxLen);

	const titleOnly = suffix.length > 0 && normalizedCore.length > maxLen - suffix.length;

	let result: string;

	if (titleOnly) {
		result =
			normalizedCore.length > maxLen
				? truncateSEOTitleAtWord(normalizedCore, maxLen)
				: normalizedCore;
	} else {
		const pieces = [normalizedCore];
		for (const piece of optional.map(normalizeSEOTitleText).filter(Boolean)) {
			const next = [...pieces, piece].join(separator);
			if (next.length <= maxLen) {
				pieces.push(piece);
			}
		}
		result = pieces.join(separator);
		if (result.length > maxLen) {
			result = truncateSEOTitleAtWord(result, maxLen);
		}
	}

	if (suffix && result.length + suffix.length <= maxLen) {
		result += suffix;
	}

	return result;
}

/** Returns true if haystack already contains needle (case-insensitive). */
export function titleContains(text: string, needle: string): boolean {
	if (!needle) return false;
	return normalizeSEOTitleText(text).toLowerCase().includes(normalizeSEOTitleText(needle).toLowerCase());
}

export interface ListingSeoLocation {
	city?: string | null;
	name?: string | null;
	province?: string | null;
}

/** Resolves the best location label for SEO: city → port name → province. */
export function resolveListingSeoLocation(port: ListingSeoLocation | null | undefined): string {
	if (!port) return '';
	return normalizeSEOTitleText(port.city ?? port.name ?? port.province ?? '');
}

export interface BuildListingDetailSeoTitleInput {
	listingTitle?: string | null;
	fallbackPrefix: string;
	boatTypeLabel: string;
	brandModel?: string;
	location?: string;
}

/**
 * Detail pages: core = armador title (or fallback), optional brand/model and city if they fit.
 */
export function buildListingDetailSeoTitle(input: BuildListingDetailSeoTitleInput): string {
	const brandModel = normalizeSEOTitleText(input.brandModel ?? '');
	const location = normalizeSEOTitleText(input.location ?? '');

	let core = normalizeSEOTitleText(input.listingTitle ?? '');
	if (!core) {
		const typePart = normalizeSEOTitleText(input.boatTypeLabel);
		if (location && typePart) {
			core = `${input.fallbackPrefix} ${typePart} en ${location}`;
		} else if (location) {
			core = `${input.fallbackPrefix} en ${location}`;
		} else if (typePart) {
			core = `${input.fallbackPrefix} ${typePart}`;
		} else {
			core = input.fallbackPrefix;
		}
	}

	const titleWithSuffix = buildSeoTitle(core, [], { includeSuffix: true });
	if (titleWithSuffix.endsWith('Zarpar')) {
		return titleWithSuffix;
	}

	if (core.length > SEO_TITLE_MAX_LEN - SEO_TITLE_SUFFIX.length) {
		return buildSeoTitle(core, [], { includeSuffix: false });
	}

	const optional: string[] = [];
	if (brandModel && !titleContains(core, brandModel)) {
		optional.push(brandModel);
	}
	if (location && !titleContains(core, location)) {
		optional.push(location);
	}

	return buildSeoTitle(core, optional, { includeSuffix: true });
}

export interface BuildMarketplaceSeoTitleInput {
	tipo?: 'alquiler' | 'experiencia' | null;
	locationLabel?: string | null;
}

/** Marketplace filter pages: search-intent titles capped at 70 chars. */
export function buildMarketplaceSeoTitle(input: BuildMarketplaceSeoTitleInput): string {
	const where = input.locationLabel ? ` en ${normalizeSEOTitleText(input.locationLabel)}` : '';

	if (input.tipo === 'experiencia') {
		return buildSeoTitle(`Experiencias náuticas${where}`, [], { includeSuffix: true });
	}
	if (input.tipo === 'alquiler') {
		return buildSeoTitle(`Alquiler de barcos${where}`, [], { includeSuffix: true });
	}

	const combined = `Alquiler de barcos y experiencias náuticas${where}`;
	if (
		input.locationLabel &&
		combined.length + SEO_TITLE_SUFFIX.length > SEO_TITLE_MAX_LEN
	) {
		return buildSeoTitle(`Alquiler de barcos${where}`, [], { includeSuffix: true });
	}
	return buildSeoTitle(combined, [], { includeSuffix: true });
}
